/*
 * Copyright (c) 2020. World Wide Generation Ltd
 */

import { useAppDispatch, useAppSelector } from '../../../reducers';
import { getSurveyGroupsSelector } from '../../../selectors/survey';
import { getInitiativeMateriality } from '../../../selectors/initiative';
import { getBlueprintFlexSearchMap } from '../../../selectors/blueprint';
import { SurveyAddAssurance } from '../../../types/survey';
import Loader from '../../loader';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useEffect } from 'react';
import { loadSurvey, unloadSurveyListSummary } from '../../../actions/survey';
import { loadBlueprintMapping } from '../../../actions/blueprints';
import { unloadInitiativeSurveyList } from '../../../slice/initiativeSurveyListSlice';
import { generateUrl } from '../../../routes/util';
import { ROUTES } from '../../../constants/routes';
import Dashboard, { DashboardRow, DashboardSection } from '../../dashboard';
import { QuestionContainerHeaderStateless } from './QuestionContainerHeaderStateless';
import { QuestionContainerFooterStateless } from './QuestionContainerFooterStateless';
import { QuestionContainerStateless } from './QuestionContainerStateless';
import { NotFoundMetric } from '../../../apps/company-tracker/components/not-found-metric';

interface QuestionContainerProps {
  handleAddAssurance: (survey: SurveyAddAssurance) => void;
}

export default function QuestionContainer(props: QuestionContainerProps) {
  const { handleAddAssurance } = props;
  const dispatch = useAppDispatch();
  const history = useHistory();
  const location = useLocation();

  const { initiativeId, surveyId, questionId, questionIndex }: { [key: string]: string } = useParams();

  const surveyGroups = useAppSelector(getSurveyGroupsSelector)
  const materiality = useAppSelector(getInitiativeMateriality);
  const blueprintState = useAppSelector((state) => state.blueprint)
  const searchIndex = useAppSelector(getBlueprintFlexSearchMap)
  const surveyState = useAppSelector((state) => state.survey);

  useEffect(() => {
    if (surveyId !== surveyState.surveyId) {
      dispatch(loadSurvey(surveyId))
    } else if (surveyState.loaded && surveyState.data.sourceName) {
      dispatch(loadBlueprintMapping(surveyState.data.sourceName))
    }
  }, [surveyId, dispatch, surveyState])

  const handleReload = async () => {
    return Promise.all([
      dispatch(loadSurvey(surveyId, true, true)),
      dispatch(unloadSurveyListSummary()),
      dispatch(unloadInitiativeSurveyList()),
    ]);
  }

  const handleGoToQuestion = ({ id, index }: { id: string, index?: number }) => {
    const url = `${generateUrl(ROUTES.COMPANY_TRACKER_SURVEY, { initiativeId, surveyId, page: 'question' })}/${id}/${index ?? ''}`;
    const searchParams = new URLSearchParams(location.search);
    history.push({
      pathname: url,
      search: searchParams.toString()
    });
  };

  if (surveyState.errored) {
    return <NotFoundMetric initiativeId={initiativeId} />
  }

  if (!surveyState.loaded || !blueprintState.loaded) {
    return <Loader />;
  }

  const survey = surveyState.data;
  const isRemovedQuestion = survey.fragmentUniversalTrackerValues.every(utrv => utrv._id !== questionId);

  if (isRemovedQuestion) {
    return <NotFoundMetric initiativeId={initiativeId} surveyId={surveyId} />
  }

  return (

    <Dashboard className='question-view'>
      <DashboardRow>
        <QuestionContainerHeaderStateless
          initiativeId={initiativeId}
          surveyId={surveyId}
          questionId={questionId}
          questionIndex={questionIndex}
          handleAddAssurance={handleAddAssurance}
          survey={survey}
          surveyGroups={surveyGroups}
          materiality={materiality}
          blueprint={blueprintState.data}
          searchIndex={searchIndex}
          handleReload={handleReload}
          handleGoToQuestion={handleGoToQuestion}
          rootBreadcrumb={{
            label: 'Overview',
            url: generateUrl(ROUTES.COMPANY_TRACKER_SURVEY, { initiativeId, surveyId, page: 'overview' }),
          }}
        />
      </DashboardRow>

      <DashboardSection className='question-wrapper' padding={2}>
        <QuestionContainerStateless
          initiativeId={initiativeId}
          surveyId={surveyId}
          questionId={questionId}
          questionIndex={questionIndex}
          survey={survey}
          surveyGroups={surveyGroups}
          materiality={materiality}
          blueprint={blueprintState.data}
          searchIndex={searchIndex}
          handleReload={handleReload}
        />
      </DashboardSection>

      <QuestionContainerFooterStateless
        initiativeId={initiativeId}
        surveyId={surveyId}
        questionId={questionId}
        questionIndex={questionIndex}
        survey={survey}
        surveyGroups={surveyGroups}
        materiality={materiality}
        blueprint={blueprintState.data}
        searchIndex={searchIndex}
      />

    </Dashboard>
  );
}
